import * as styles from 'owa-timestream-asset-sdk-filepicker/lib/components/styles.scss';
import type { FileLinkPayload } from '../schema';
import { getIconForFile } from 'owa-file-icon';
import { ControlIcons } from 'owa-control-icons';
import {
    AssetIcon,
    AssetError,
    AssetErrorType,
    AssetErrorRetryable,
} from 'owa-timestream-asset-sdk';
import type { AssetCommonViewProps } from 'owa-timestream-asset-sdk-types';
import { observer } from 'mobx-react-lite';
import { Link } from '@fluentui/react/lib/Link';
import classNames from 'classnames';
import * as React from 'react';
import { filePlaceholderText, assetErrorNoLink } from '../filepickerStrings.locstring.json';
import FileLinkThumbnailView from './FileLinkThumbnailView';
import FileLinkModifyInfoView from './FileLinkModifyInfoView';
import useFileLinkViewState from '../hooks/useFileLinkViewState';
import loc from 'owa-localize';

/** Renders a Link asset */
export default observer(function FileLinkView(props: AssetCommonViewProps<FileLinkPayload>) {
    const {
        filePickerLinkData,
        url,
        thumbnailUrl,
        lastModifiedInfo,
        assetErrorProps,
        retryFetchUrlInfo,
    } = useFileLinkViewState(props);

    if (assetErrorProps.errorType && assetErrorProps.errorType !== AssetErrorType.Retryable) {
        return <AssetError {...assetErrorProps} />;
    }

    const iconCSS: string = getIconForFile(
        filePickerLinkData.fileName,
        filePickerLinkData.isFolder,
        filePickerLinkData.mimeType
    ).small;

    if (props.isNewlyCreated || (!filePickerLinkData.fileName && !filePickerLinkData.mimeType)) {
        return (
            <div className={styles.emptyAsset}>
                <AssetIcon iconName={ControlIcons.Attach} className={styles.attachIcon} />
                <div className={styles.placeholderText}>{loc(filePlaceholderText)}</div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {filePickerLinkData?.fileName ? (
                <>
                    <FileLinkThumbnailView thumbnailUrl={thumbnailUrl} />
                    {props.DueDate}
                    <div
                        className={classNames(styles.iconLinkContainer, {
                            [styles.iconLinkContainerMargin]: !!thumbnailUrl,
                        })}>
                        <div className={classNames(iconCSS, { [styles.icon]: !!url })} />
                        <div className={styles.url} title={url}>
                            {url}
                        </div>
                    </div>
                    <Link
                        className={styles.title}
                        href={url}
                        target="_blank"
                        draggable={false}
                        title={filePickerLinkData.fileName}>
                        {filePickerLinkData.fileName}
                    </Link>
                    {lastModifiedInfo && <FileLinkModifyInfoView {...lastModifiedInfo} />}
                    {assetErrorProps.errorType === AssetErrorType.Retryable && (
                        <AssetErrorRetryable
                            errorDesc={loc(assetErrorNoLink)}
                            retryCallback={retryFetchUrlInfo}
                        />
                    )}
                </>
            ) : (
                <AssetError assetType={'file'} />
            )}
        </div>
    );
});
